import { useRef, useState } from 'react';
import { HOSTS, plural, statusColor, statusLabel } from '../format';
import { useApp } from '../store';
import { btn, cellBorder, input, mono, panelCol, primary, sectionTitle } from '../ui';

export function Files() {
  const app = useApp();
  const [dragOver, setDragOver] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const job = app.job?.kind === 'files' ? app.job : null;
  const ok = job?.targets.filter(t => t.status === 'done').length ?? 0;
  const err = job?.targets.filter(t => t.status === 'error').length ?? 0;
  const total = job?.targets.length ?? 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'start' }}>
      <section style={panelCol}>
        <span style={sectionTitle}>Пакет файлов</span>

        <div
          onClick={() => picker.current?.click()}
          onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files.length) void app.addFiles(e.dataTransfer.files);
          }}
          style={{
            border: `1px dashed ${dragOver ? 'var(--acc)' : 'var(--line2)'}`,
            borderRadius: 'var(--r)',
            padding: '30px 22px',
            background: dragOver ? 'var(--accSoft)' : 'var(--panel2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            cursor: 'pointer'
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14.5 }}>Перетащите файлы или папку</span>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
            до 4 ГБ на пакет · одинаковые файлы не копируются повторно (сверка по SHA-256)
          </span>
        </div>
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          onChange={e => {
            if (e.target.files?.length) void app.addFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {app.payload.map(f => (
            <div
              key={f.sha256}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto',
                gap: 14,
                padding: '9px 0',
                ...cellBorder,
                ...mono,
                fontSize: 11.5,
                alignItems: 'baseline'
              }}
            >
              <span>{f.name}</span>
              <span style={{ color: 'var(--ink3)' }}>{f.size}</span>
              <span style={{ color: 'var(--ink3)' }}>{f.kind}</span>
              <button
                onClick={() => app.removeFile(f.sha256)}
                title="Убрать из пакета"
                style={{ ...btn, color: 'var(--ink4)', fontSize: 12 }}
                className="hovInk"
              >
                ×
              </button>
            </div>
          ))}
          {!app.payload.length && (
            <span style={{ fontSize: 11.5, color: 'var(--ink4)' }}>Пакет пуст — добавьте файлы.</span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>Путь назначения</span>
          <input
            className="focusAcc"
            value={app.destPath}
            onChange={e => app.setDestPath(e.target.value)}
            placeholder="C:\путь\на\сервере\"
            style={input}
          />
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', ...mono, fontSize: 11, color: 'var(--ink3)' }}>
          <span>перезапись: если новее</span>
          <span>
            после копирования:{' '}
            {app.payload.some(f => f.name.toLowerCase().endsWith('install.ps1')) ? 'install.ps1' : '—'}
          </span>
        </div>

        <button
          className="bright"
          onClick={() => void app.runFiles()}
          disabled={!app.selCount || !app.payload.length}
          style={{ ...primary, width: 'max-content' }}
        >
          Отправить на {plural(app.selCount, HOSTS)}
        </button>
      </section>

      <section style={panelCol}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <span style={sectionTitle}>Передача</span>
          <span style={{ ...mono, fontSize: 11, color: 'var(--ink3)' }}>
            {job ? `${ok} готово · ${err} ошибок · ${total - ok - err} в работе` : '—'}
          </span>
        </div>

        {job ? (
          <div style={{ maxHeight: 560, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {job.targets.map(t => (
              <div
                key={t.hostId}
                title={t.out || undefined}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '158px 1fr 74px',
                  gap: 12,
                  padding: '10px 0',
                  ...cellBorder,
                  ...mono,
                  fontSize: 11.5,
                  alignItems: 'center'
                }}
              >
                <span>{t.name}</span>
                <span style={{ display: 'block', height: 5, borderRadius: 99, background: 'var(--track)' }}>
                  <span
                    style={{
                      display: 'block',
                      height: 5,
                      borderRadius: 99,
                      background: statusColor(t.status),
                      width: t.pct + '%'
                    }}
                  />
                </span>
                <span style={{ textAlign: 'right', color: statusColor(t.status) }}>{statusLabel(t.status)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: '40px 18px',
              borderRadius: 'var(--rs)',
              background: 'var(--panel2)',
              fontSize: 12.5,
              color: 'var(--ink3)'
            }}
          >
            Прогресс по каждому хосту появится после запуска.
          </div>
        )}
      </section>
    </div>
  );
}
